import { IconBrandLinkedin, IconMail, IconPhone } from '@tabler/icons-react';

import { Outlet, createFileRoute } from '@tanstack/react-router';

import { Anchor, Box, Container, Group, Image } from '@optiro/ui';

import { ExperiencesProvider } from '@/providers/ExperiencesProvider';

import logo from './optiro.svg';

type ExperiencesSearchParams = {
  returnUrl?: string;
};

export const Route = createFileRoute('/experiences')({
  component: LayoutComponent,
  errorComponent: ErrorComponent,
  validateSearch: (search: Record<string, unknown>): ExperiencesSearchParams => {
    return {
      returnUrl: search.returnUrl as string,
    };
  },
});

const GANTT_BARS: Array<{
  top: number;
  left: number;
  width: number;
  delay: number;
  duration: number;
}> = [
  { top: 6, left: 4, width: 22, delay: 0, duration: 14 },
  { top: 6, left: 32, width: 14, delay: 3.5, duration: 16 },
  { top: 6, left: 68, width: 19, delay: 8.0, duration: 13 },
  { top: 19, left: 8, width: 31, delay: 2.0, duration: 15 },
  { top: 19, left: 54, width: 24, delay: 10.0, duration: 12 },
  { top: 32, left: 2, width: 17, delay: 5.5, duration: 16 },
  { top: 32, left: 26, width: 36, delay: 1.0, duration: 14 },
  { top: 32, left: 71, width: 16, delay: 12.0, duration: 13 },
  { top: 45, left: 5, width: 41, delay: 7.0, duration: 15 },
  { top: 45, left: 52, width: 28, delay: 0.0, duration: 12 },
  { top: 58, left: 9, width: 22, delay: 11.0, duration: 14 },
  { top: 58, left: 37, width: 29, delay: 3.0, duration: 16 },
  { top: 58, left: 74, width: 13, delay: 6.5, duration: 13 },
  { top: 71, left: 3, width: 33, delay: 2.5, duration: 15 },
  { top: 71, left: 43, width: 21, delay: 9.0, duration: 12 },
  { top: 84, left: 1, width: 20, delay: 5.0, duration: 14 },
  { top: 84, left: 27, width: 38, delay: 1.5, duration: 16 },
  { top: 84, left: 72, width: 16, delay: 13.0, duration: 13 },
];

const GRID_ROWS = [6, 19, 32, 45, 58, 71, 84];

function GanttBackground() {
  return (
    <>
      <style>{`
        @keyframes gantt-draw {
          0%   { transform: scaleX(0); opacity: 0; }
          10%  { opacity: 0.09; }
          65%  { transform: scaleX(1); opacity: 0.07; }
          85%  { transform: scaleX(1); opacity: 0.07; }
          100% { transform: scaleX(1); opacity: 0; }
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        {GRID_ROWS.map((top) => (
          <div
            key={top}
            style={{
              position: 'absolute',
              top: `calc(${top}% + 16px)`,
              left: 0,
              right: 0,
              height: 1,
              backgroundColor: 'rgba(150, 128, 100, 0.04)',
            }}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '38%',
            width: 1,
            backgroundColor: 'rgba(150, 128, 100, 0.06)',
          }}
        />
        {GANTT_BARS.map(({ top, left, width, delay, duration }, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: `${top}%`,
              left: `${left}%`,
              width: `${width}%`,
              height: 8,
              borderRadius: 4,
              backgroundColor:
                i % 3 === 0
                  ? 'rgba(140, 118, 92, 0.9)'
                  : i % 3 === 1
                    ? 'rgba(120, 140, 110, 0.9)'
                    : 'rgba(160, 130, 100, 0.9)',
              transformOrigin: 'left center',
              animation: `gantt-draw ${duration}s ease-out ${delay}s infinite both`,
            }}
          />
        ))}
      </div>
    </>
  );
}

function LayoutComponent() {
  return (
    <ExperiencesProvider values={{ returnUrl: Route.useSearch().returnUrl }}>
      <div
        style={{ minHeight: '100vh', backgroundColor: 'rgb(248, 245, 239)', position: 'relative' }}
      >
        <GanttBackground />
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Box
            style={{
              backgroundColor: 'rgb(248, 245, 239)',
            }}
          >
            <Container size="sm">
              <Group justify="center" py="md">
                <Image src={logo} alt="Optiro" h={32} w="auto" />
              </Group>
            </Container>
          </Box>
          <Outlet />
          <Container size="sm">
            <Group justify="center" gap="xl" py="xl">
              <Anchor
                size="sm"
                c="dimmed"
                href="mailto:support@optiro.dk"
                target="_blank"
                style={{ textDecoration: 'none' }}
              >
                <Group gap={6}>
                  <IconMail size={16} />
                  support@optiro.dk
                </Group>
              </Anchor>
              <Anchor
                size="sm"
                c="dimmed"
                href="tel:+4526122824"
                target="_blank"
                style={{ textDecoration: 'none' }}
              >
                <Group gap={6}>
                  <IconPhone size={16} />
                  +45 26 12 28 24
                </Group>
              </Anchor>
              <Anchor
                size="sm"
                c="dimmed"
                href="https://linkedin.com/company/optiro-planning"
                target="_blank"
                style={{ textDecoration: 'none' }}
              >
                <Group gap={6}>
                  <IconBrandLinkedin size={16} />
                  LinkedIn
                </Group>
              </Anchor>
            </Group>
          </Container>
        </div>
      </div>
    </ExperiencesProvider>
  );
}

function ErrorComponent() {
  return <div>Something went wrong!</div>;
}
